import websocketService from "./websocketService";
import { WebSocketScope } from "../utils/enums";

/**
 * WebSocket Scope Handler
 * 
 * Responsabilidad: Parsear mensajes WebSocket y emitir eventos organizados por scope
 * Bajo acoplamiento: Solo depende de websocketService
 * Alta cohesión: Solo maneja lógica de scopes y eventos
 * 
 * Esta capa se encarga de:
 * - Validar la estructura de los mensajes
 * - Validar que el scope sea uno de los permitidos
 * - Normalizar los mensajes
 * - Emitir eventos organizados por scope y event
 */
class WebSocketScopeHandler {
  constructor() {
    this.isInitialized = false;
    this.handleMessageBound = this.handleMessage.bind(this);
  }

  /**
   * Inicializa el handler y se suscribe a mensajes del servicio
   */
  initialize() {
    if (this.isInitialized) {
      console.warn("[WebSocketScopeHandler] Already initialized");
      return;
    }

    websocketService.on("message", this.handleMessageBound);
    this.isInitialized = true;
    console.log("[WebSocketScopeHandler] ✅ Initialized");
  }

  /**
   * Parsea y enruta mensajes por scope
   * @param {Object} rawData - Mensaje WebSocket parseado
   */
  handleMessage(rawData) {
    // Extraer el data si el mensaje tiene estructura { type: "...", data: {...} }
    // El mensaje puede tener type: "message", type: "DELTA", etc., pero si tiene data, lo usamos
    let messageData = rawData;
    if (rawData && rawData.data && typeof rawData.data === 'object') {
      // Si rawData.data tiene scope y event, es la estructura correcta
      if (rawData.data.scope && rawData.data.event) {
      messageData = rawData.data;
      // Preservar timestamp del nivel superior si existe
      if (rawData.timestamp && !messageData.timestamp) {
        messageData.timestamp = rawData.timestamp;
        }
      }
    }

    // Validar estructura del mensaje
    if (!this.isValidMessage(messageData)) {
      console.warn("[WebSocketScopeHandler] ⚠️ Invalid message format", rawData);
      return;
    }

    const { scope, event, data: payloadData, action, payload, metadata, id, ref_id: dataRefId, timestamp, sequence } = messageData;
    const ref_id = dataRefId ?? rawData?.ref_id;

    // Normalize scope to uppercase (backend sends title-case e.g. "Conversation")
    const normalizedScope = String(scope ?? '').toUpperCase();

    // El payload puede estar en 'data' o 'payload', priorizar 'data' si existe
    const finalPayload = payloadData || payload || {};

    // Estructura normalizada del mensaje
    const normalizedMessage = {
      id,
      scope: normalizedScope,
      event,
      action, // Incluir action si existe
      payload: finalPayload,
      metadata: metadata || {},
      ref_id,
      sequence,
      timestamp: timestamp || rawData.timestamp,
    };

    console.log(`[WebSocketScopeHandler] 📨 Scope: ${normalizedScope}, Event: ${event}`, {
      id,
      ref_id,
      timestamp: normalizedMessage.timestamp,
      hasPayload: !!finalPayload && Object.keys(finalPayload).length > 0,
      action,
    });

    // Emitir eventos organizados por scope (use normalizedScope for case-insensitive routing)
    // 1. Evento genérico por scope (todos los eventos de un scope)
    websocketService.emit(`scope:${normalizedScope}`, normalizedMessage);

    // 2. Evento específico por scope + event (más granular)
    websocketService.emit(`scope:${normalizedScope}:${event}`, normalizedMessage);

    // 3. Evento genérico para todos los eventos de un tipo (cross-scope)
    websocketService.emit(`event:${event}`, normalizedMessage);
  }

  /**
   * Valida la estructura del mensaje según el estándar definido
   * @param {Object} data - Mensaje a validar (ya extraído de data si venía envuelto)
   * @returns {boolean} - true si el mensaje es válido
   */
  isValidMessage(data) {
    // Validar estructura básica
    if (
      !data ||
      typeof data !== "object" ||
      !data.scope ||
      !data.event ||
      typeof data.scope !== "string" ||
      typeof data.event !== "string"
    ) {
      return false;
    }

    // Validar que el scope sea uno de los permitidos (case-insensitive)
    const validScopes = Object.values(WebSocketScope);
    const scopeUpper = String(data.scope ?? '').toUpperCase();
    if (!validScopes.includes(scopeUpper)) {
      console.warn(
        `[WebSocketScopeHandler] ⚠️ Invalid scope received: ${data.scope}. Valid scopes: ${validScopes.join(", ")}`
      );
      return false;
    }

    return true;
  }

  /**
   * Limpia el handler y desuscribe los listeners
   */
  cleanup() {
    if (this.isInitialized) {
      websocketService.off("message", this.handleMessageBound);
      this.isInitialized = false;
      console.log("[WebSocketScopeHandler] 🧹 Cleaned up");
    }
  }
}

// Export singleton
export const websocketScopeHandler = new WebSocketScopeHandler();
export default websocketScopeHandler;

